import React from "react";
import { ColDef, ICellRendererParams, ValueGetterParams } from "ag-grid-community";
import { ISitePage } from "../../../laco-common/base/models/page";
import { ISelectFormField, IForm, IFormField, ISection, ClientFirebase } from "../../../laco-common";
import { getColDefsFromConfig, ColumnConfigurationMap } from "./utils";
import { WithOptional } from "../../../laco-common/helpers/extendedTypes";
import {
	FormField,
	FormValidation,
	ICustomValidatorResult,
	Validators,
	ErrorType,
} from "../../../laco-common/base/form-validation";
import { CorreColDef } from "../colDef";
import {
	DynamicFormField,
	DynamicFormValidation,
	SelectOption,
} from "../../../laco-common/base/form-validation/dynamic";
import { getForm } from "../../../laco-common/base/firestore-utils";

export const getFieldsColDef = (form: IForm): (ColDef & IFormField)[] => {
	return [
		{
			id: "fields",
			colId: "fields",
			field: "fields",
			headerName: "Form Fields",
			fieldType: "list",
			listType: "formField",
			optional: true,
			order: 210,
			label: "Form Fields",
			valueGetter: (params: ValueGetterParams) => {
				return (form.fields || [])
					.map((field: IFormField) => {
						return field.label;
					})
					.join(", ");
			},
		},
		{
			id: "sections",
			colId: "sections",
			field: "sections",
			headerName: "Sections",
			fieldType: "list",
			listType: "formSection",
			optional: true,
			order: 200,
			label: "Sections",
			cellRendererFramework: (params: ICellRendererParams) => {
				return (
					<div className="d-flex flex-column flex-md-row">
						{(form.sections || []).map((section: ISection) => {
							return (
								<ul key={section.id} className="simple-list mr-2" style={{ minWidth: "200px" }}>
									<li>
										<h5>{section.name}</h5>
									</li>
									{(section.fields || []).map((field: IFormField) => {
										return <li>{field.label}</li>;
									})}
								</ul>
							);
						})}
					</div>
				);
			},
		},
	];
};

export const getFormColDefinitions = (isPS: boolean, clientFirebase: ClientFirebase): ColumnConfigurationMap => {
	const canEdit = null; //(page: ICMSPage): boolean => page.id !== "root";

	const fieldsMap: Partial<
		Record<
			keyof IForm,
			WithOptional<ISelectFormField, "id" | "order" | "label" | "fieldType" | "selectValues"> &
				ColDef &
				Partial<CorreColDef> &
				Partial<FormField> &
				Partial<DynamicFormField>
		>
	> = {
		name: { label: "Name", placeholder: "Enter the form name", canEdit },
		description: {
			label: "Description",
			optional: true,
			placeholder: "Optional description",
			canEdit,
			fieldType: "textarea",
			displayContentPreview: true,
		},
		order: { label: "Order", fieldType: "number", canEdit },
		active: { label: "Active", fieldType: "boolean", canEdit },
		omitContactFields: {
			label: "Omit User Name and Email Fields",
			fieldType: "boolean",
			canEdit,
		},
		hide: { label: "Hide", fieldType: "boolean", optional: true, canEdit },
		hideSubmit: { label: "Hide Submit Button", fieldType: "boolean", optional: true, canEdit },
		btnText: { label: "Button Text", fieldType: "text", optional: true, canEdit },
		successMsg: { label: "Submission Success Message", fieldType: "text", optional: true, canEdit },
		department_id: {
			label: "Department",
			fieldType: "autocomplete",
			collectionName: "departments",
			collectionDisplayField: "name",
			freeSolo: true,
			isDepartment: true,
			optional: true,
			canEdit,
		},
		ecwidSku: {
			label: "Ecwid Product Id (not SKU)",
			fieldType: "number",
			optional: true,
			canEdit,
		},
		productPrice: {
			label: "Product Price",
			fieldType: "number",
			optional: true,
			canEdit,
		},
		priceMultiplierField: {
			label: "Price Multiplier Field",
			fieldType: "select",
			optional: true,
			getSelectOptions: async (authContext, form: DynamicFormValidation): Promise<SelectOption[]> => {
				const results = await clientFirebase
					.getFirebase()
					.firestore()
					.collection("forms")
					.where("name", "==", form.fields.name.value)
					.where("order", "==", form.fields.order.value)
					.get();
				if (!results?.docs?.length) return [];
				const formId: string = results?.docs[0].id;
				const formConfig: IForm = await getForm(formId, clientFirebase.getFirebase());
				const options: SelectOption[] = [];
				console.log("formConfig.fields:", formConfig.fields);
				formConfig.fields.forEach((field: IFormField) => {
					switch (field.id) {
						case "user-name":
						case "user-email":
						case "email":
						case "name": {
							break;
						}
						default: {
							if (field.fieldType === "number") options.push({ label: field.label, value: field.id });
						}
					}
				});
				return Promise.resolve(options);
			},
			canEdit,
		},
		sendEmailTo: {
			label: "Send Email To",
			fieldType: "chips",
			customValidator: (field: FormField, form: FormValidation): ICustomValidatorResult => {
				const emails: string[] = field.value || [];
				let valid: boolean = true;
				emails.forEach((email: string) => {
					if (!Validators.isValidEmail(email)) {
						valid = false;
					}
				});
				if (!valid)
					return {
						field,
						errType: ErrorType.Email,
						msg: "List contains an invalid email address.",
					};
				return {
					field,
				};
			},
			minWidth: 400,
			optional: true,
			canEdit,
		},
		fields: {
			label: "Form Fields",
			fieldType: "list",
			listType: "formField",
			optional: true,
			canEdit,
			omitInGridView: true,
		},
		sections: {
			label: "Sections",
			fieldType: "list",
			listType: "formSection",
			optional: true,
			canEdit,
			omitInGridView: true,
		},
	};

	if (isPS) {
		fieldsMap.psRoles = {
			label: "PS Roles",
			optional: true,
			fieldType: "chips",
			selectValues: [
				{ label: "Guest", value: "guest" },
				{ label: "Member", value: "member" },
				{ label: "Agent", value: "agent" },
				{ label: "Admin", value: "admin" },
			],
		};
		delete fieldsMap.department_id;
	}

	let i: number = 0;
	for (let field in fieldsMap) {
		fieldsMap[field].id = field;
		fieldsMap[field].order = i;
		i++;
	}

	// const colDefs: ColDef[] = getColDefsFromConfig(fieldsMap as ColumnConfigurationMap, "forms");
	// return colDefs;
	return fieldsMap;
};
